/*
 * quote-button-monitor.js
 *
 * Observability for the "Request a Formal Quote" button. Its JOB IS NOT TO
 * FIX anything — quote-button-safety.js already retries a stuck click. This
 * script just TELLS YOU how often the button misbehaves, so you stop relying
 * on customers reporting it and can see the real failure rate in analytics.
 *
 * What it does:
 *   - On a genuine (user-trusted) click of the quote button, it starts a timer.
 *   - If the Quotify form opens, it reports how long that took
 *     (quote_button_opened, with time_to_open_ms) — useful for spotting slow
 *     loads before they become full failures.
 *   - If the form still hasn't opened after TIMEOUT_MS (longer than the safety
 *     script's own retry window), it reports a failure (quote_button_failed).
 *
 * Where the data goes:
 *   - window.gtag('event', ...) — shows up in GA4 (and is visible to Google
 *     Ads if linked). Uses the same global gtag the conversion tracker uses.
 *   - window.dataLayer.push(...) — in case you prefer GTM.
 *   - console.warn on failure — so it's also visible in DevTools during testing.
 *
 * Safe by design:
 *   - Only reacts to event.isTrusted clicks, so the safety script's synthetic
 *     retry clicks never register as new attempts (no inflated counts).
 *   - One outcome per click; never double-reports.
 *   - Pure listener + timers; does not touch the button or the form.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 9000;   // longer than safety.js's ~7.5s retry window
  var POLL_MS = 250;

  // --- identical "is this the quote button?" logic as quote-button-safety.js
  function isQuoteTrigger(el) {
    if (!el || !el.closest) return false;
    if (el.closest('.formalquote2link, .formalquote2')) return false;
    if (el.closest('#quote-button-wrapper')) return true;
    var btn = el.closest('button, a.button, a.btn, .btn, [role="button"]');
    if (!btn) return false;
    var text = (btn.textContent || '').trim().toLowerCase();
    return text === 'request a formal quote' || text === 'request quote';
  }

  // --- identical "did the form open?" logic as quote-button-safety.js
  function quoteFormOpen() {
    var f = document.getElementById('quotify-frame');
    if (f) {
      var r = f.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
    }
    var alt = document.querySelector(
      'iframe[src*="quotify"], [class*="quotify-modal"], [id*="quotify-modal"]'
    );
    if (alt) {
      var ar = alt.getBoundingClientRect();
      if (ar.width > 0 && ar.height > 0) return true;
    }
    return false;
  }

  function report(eventName, params) {
    params = params || {};
    // GA4 / Google Ads (same global tag the conversion tracker uses)
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
    } catch (_) {}
    // GTM fallback
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (_) {}
  }

  var watching = false; // one attempt tracked at a time

  function watchClick() {
    if (watching) return;         // already timing an attempt
    if (quoteFormOpen()) return;  // form already open (e.g. re-click) — ignore
    watching = true;

    var start = Date.now();
    var pagePath = (window.location && window.location.pathname) || '';

    var timer = setInterval(function () {
      var elapsed = Date.now() - start;

      if (quoteFormOpen()) {
        clearInterval(timer);
        watching = false;
        report('quote_button_opened', {
          event_category: 'quote_button',
          time_to_open_ms: elapsed,
          slow: elapsed > 2000,     // flag noticeably slow opens
          page_path: pagePath
        });
        return;
      }

      if (elapsed >= TIMEOUT_MS) {
        clearInterval(timer);
        watching = false;
        try {
          console.warn('[quote-button-monitor] form did not open within ' +
            TIMEOUT_MS + 'ms on ' + pagePath);
        } catch (_) {}
        report('quote_button_failed', {
          event_category: 'quote_button',
          time_waited_ms: elapsed,
          page_path: pagePath
        });
      }
    }, POLL_MS);
  }

  document.addEventListener('click', function (event) {
    // Only real user clicks — ignore the safety script's synthetic retries.
    if (!event.isTrusted) return;
    var target = event.target;
    if (!isQuoteTrigger(target)) return;
    watchClick();
  }, true);
})();
